import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const PersonalNotifications = ({ settings, memberColor, onChange }) => {
  const timingOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: '5min', label: '5 minutes before' },
    { value: '15min', label: '15 minutes before' },
    { value: '30min', label: '30 minutes before' },
    { value: '1hour', label: '1 hour before' },
    { value: 'digest', label: 'Daily Digest' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent Only' }
  ];

  const deliveryMethodOptions = [
    { value: 'push', label: 'Push Notification' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' }
  ];

  const reminderFrequencyOptions = [
    { value: '5min', label: '5 minutes before' },
    { value: '15min', label: '15 minutes before' },
    { value: '30min', label: '30 minutes before' },
    { value: '1hour', label: '1 hour before' },
    { value: '2hours', label: '2 hours before' },
    { value: '1day', label: '1 day before' },
    { value: '2days', label: '2 days before' },
    { value: '1week', label: '1 week before' }
  ];

  const deliveryTimingOptions = [
    { value: 'smart', label: 'Smart (respects quiet hours)' },
    { value: 'exact', label: 'Exact time' },
    { value: 'morning', label: 'Morning digest (8 AM)' },
    { value: 'evening', label: 'Evening digest (6 PM)' }
  ];

  const toggleDeliveryMethod = (method) => {
    const currentMethods = settings?.deliveryMethod || [];
    const newMethods = currentMethods?.includes(method)
      ? currentMethods?.filter(m => m !== method)
      : [...currentMethods, method];
    onChange({ deliveryMethod: newMethods });
  };

  const updateNotificationCategory = (category, updates) => {
    onChange({
      [category]: {
        ...settings?.[category],
        ...updates
      }
    });
  };

  const toggleReminderFrequency = (frequency) => {
    const currentFrequencies = settings?.reminderFrequency || [];
    const newFrequencies = currentFrequencies?.includes(frequency)
      ? currentFrequencies?.filter(f => f !== frequency)
      : [...currentFrequencies, frequency];
    onChange({ reminderFrequency: newFrequencies });
  };

  return (
    <div className="space-y-6">
      {/* Smart Reminder Notifications Section */}
      <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-5">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="bell" size={24} style={{ color: memberColor }} />
          <div>
            <h2 className="text-h3 font-heading text-foreground">
              Smart Reminder Notifications
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Customize reminder frequency and delivery timing
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Reminder Frequency */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Reminder Frequency
            </label>
            <div className="grid grid-cols-2 gap-2">
              {reminderFrequencyOptions?.map(option => (
                <label
                  key={option?.value}
                  className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={settings?.reminderFrequency?.includes(option?.value) || false}
                    onCheckedChange={() => toggleReminderFrequency(option?.value)}
                  />
                  <span className="text-sm text-foreground">{option?.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Delivery Timing */}
          <div>
            <Select
              label="Delivery Timing"
              description="Choose when to receive notifications"
              value={settings?.deliveryTiming || 'smart'}
              onChange={(value) => onChange({ deliveryTiming: value })}
              options={deliveryTimingOptions}
            />
          </div>

          {/* Quiet Hours */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Quiet Hours
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="time"
                label="Start"
                value={settings?.quietHoursStart || '22:00'}
                onChange={(e) => onChange({ quietHoursStart: e?.target?.value })}
              />
              <Input
                type="time"
                label="End"
                value={settings?.quietHoursEnd || '07:00'}
                onChange={(e) => onChange({ quietHoursEnd: e?.target?.value })}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              No notifications will be sent during quiet hours
            </p>
          </div>

          {/* Recurring Tasks Toggle */}
          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="repeat" size={18} className="text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Notify for Recurring Items
                </p>
                <p className="text-xs text-muted-foreground">
                  Receive reminders for recurring tasks and events
                </p>
              </div>
            </div>
            <button
              onClick={() => onChange({ notifyForRecurring: !settings?.notifyForRecurring })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings?.notifyForRecurring ? 'bg-primary' : 'bg-muted'
              }`}
              aria-label="Toggle recurring notifications"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings?.notifyForRecurring ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Message Notifications */}
      <div className="bg-muted/30 rounded-lg p-4 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="message-square" size={20} style={{ color: memberColor }} />
            <h3 className="font-semibold text-foreground">Message Notifications</h3>
          </div>
          <button
            onClick={() => updateNotificationCategory('messages', { 
              enabled: !settings?.messages?.enabled 
            })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings?.messages?.enabled ? 'bg-primary' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.messages?.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {settings?.messages?.enabled && (
          <div className="space-y-3 ml-7">
            <Checkbox
              checked={settings?.messages?.sound || false}
              onCheckedChange={(checked) => updateNotificationCategory('messages', { sound: checked })}
              label="Sound"
            />
            <Select
              label="Timing"
              value={settings?.messages?.timing || 'immediate'}
              onChange={(value) => updateNotificationCategory('messages', { timing: value })}
              options={timingOptions}
            />
          </div>
        )}
      </div>

      {/* Task Reminders */}
      <div className="bg-muted/30 rounded-lg p-4 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="check-square" size={20} style={{ color: memberColor }} />
            <h3 className="font-semibold text-foreground">Task Reminders</h3>
          </div>
          <button
            onClick={() => updateNotificationCategory('taskReminders', { 
              enabled: !settings?.taskReminders?.enabled 
            })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings?.taskReminders?.enabled ? 'bg-primary' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.taskReminders?.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {settings?.taskReminders?.enabled && (
          <div className="space-y-3 ml-7">
            <Checkbox
              checked={settings?.taskReminders?.sound || false}
              onCheckedChange={(checked) => updateNotificationCategory('taskReminders', { sound: checked })}
              label="Sound"
            />
            <Select
              label="Reminder Timing"
              value={settings?.taskReminders?.timing || '15min'}
              onChange={(value) => updateNotificationCategory('taskReminders', { timing: value })}
              options={timingOptions}
            />
          </div>
        )}
      </div>

      {/* Calendar Alerts */}
      <div className="bg-muted/30 rounded-lg p-4 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="calendar" size={20} style={{ color: memberColor }} />
            <h3 className="font-semibold text-foreground">Calendar Alerts</h3>
          </div>
          <button
            onClick={() => updateNotificationCategory('calendarAlerts', { 
              enabled: !settings?.calendarAlerts?.enabled 
            })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings?.calendarAlerts?.enabled ? 'bg-primary' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.calendarAlerts?.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {settings?.calendarAlerts?.enabled && (
          <div className="space-y-3 ml-7">
            <Checkbox
              checked={settings?.calendarAlerts?.sound || false}
              onCheckedChange={(checked) => updateNotificationCategory('calendarAlerts', { sound: checked })}
              label="Sound"
            />
            <Select
              label="Alert Timing"
              value={settings?.calendarAlerts?.timing || '30min'}
              onChange={(value) => updateNotificationCategory('calendarAlerts', { timing: value })}
              options={timingOptions}
            />
          </div>
        )}
      </div>

      {/* Family Activity Updates */}
      <div className="bg-muted/30 rounded-lg p-4 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="users" size={20} style={{ color: memberColor }} />
            <h3 className="font-semibold text-foreground">Family Activity Updates</h3>
          </div>
          <button
            onClick={() => updateNotificationCategory('familyActivity', { 
              enabled: !settings?.familyActivity?.enabled 
            })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings?.familyActivity?.enabled ? 'bg-primary' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.familyActivity?.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {settings?.familyActivity?.enabled && (
          <div className="space-y-3 ml-7">
            <Checkbox
              checked={settings?.familyActivity?.sound || false}
              onCheckedChange={(checked) => updateNotificationCategory('familyActivity', { sound: checked })}
              label="Sound"
            />
            <Select
              label="Update Frequency"
              value={settings?.familyActivity?.timing || 'digest'}
              onChange={(value) => updateNotificationCategory('familyActivity', { timing: value })}
              options={timingOptions}
            />
          </div>
        )}
      </div>

      {/* Delivery Method */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="send" size={20} style={{ color: memberColor }} />
          Delivery Method
        </h3>
        <CheckboxGroup label="Select notification delivery methods">
          {deliveryMethodOptions?.map(option => (
            <Checkbox
              key={option?.value}
              checked={settings?.deliveryMethod?.includes(option?.value) || false}
              onCheckedChange={() => toggleDeliveryMethod(option?.value)}
              label={option?.label}
            />
          ))}
        </CheckboxGroup>
      </div>

      {/* Advanced Settings */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="settings" size={20} style={{ color: memberColor }} />
          Advanced Settings
        </h3>
        <div className="space-y-4">
          <Select
            label="Notification Priority Level"
            value={settings?.priority || 'normal'}
            onChange={(value) => onChange({ priority: value })}
            options={priorityOptions}
            description="Filter notifications based on importance"
          />
          <Checkbox
            checked={settings?.crossDeviceSync || false}
            onCheckedChange={(checked) => onChange({ crossDeviceSync: checked })}
            label="Cross-Device Synchronization"
            description="Sync notification preferences across all your devices"
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalNotifications;